import { Box, Flex, Heading, Image, Text, VisuallyHidden } from '@chakra-ui/react';
import { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { H1 } from '@/components/Heading';
import { LogoIcon } from '@/components/Icons';
import { LoginForm } from '@/components/Login/LoginForm';
import { Button } from '@/components/UI/Buttons/Button';

import { AppService } from '@/services';

const personImageSrc = AppService.getPathWithCountryCode('/images/login-power-panel-person.webp');
const bgImageSrc = AppService.getPathWithCountryCode('/images/login-power-panel-bg.webp');

const BrandLogo: FunctionComponent<{ w?: string }> = ({ w = '300px' }) => (
    <LogoIcon w={w} h="auto" color="black" />
);

const RepresentativeContact: FunctionComponent<{ light?: boolean; large?: boolean }> = ({
    light = false,
    large = false
}) => (
    <Box color={light ? 'white' : 'black'} textAlign="center">
        <Text fontSize={large ? '36px' : 'lg'} fontWeight="semibold">
            <FormattedMessage id="login-power-panel.rep-name" />
        </Text>
        <Text fontSize={large ? '28px' : 'sm'} mb={large ? 8 : 3}>
            <FormattedMessage id="login-power-panel.rep-department" />
        </Text>
        <Text fontSize={large ? '28px' : 'sm'}>
            tel: <FormattedMessage id="login-power-panel.rep-phone" />
        </Text>
        <Text fontSize={large ? '28px' : 'sm'}>
            <FormattedMessage id="login-power-panel.rep-email" />
        </Text>
    </Box>
);

const TeaserCta: FunctionComponent<{ scheduleCallUrl?: string }> = ({ scheduleCallUrl }) => (
    <>
        <Heading
            as="h2"
            fontWeight="normal"
            fontSize={{ base: '2xl', lg: '36px' }}
            lineHeight={1.3}
            color="white"
            textAlign="center"
            whiteSpace="pre-line"
        >
            <FormattedMessage id="login-power-panel.title" />
        </Heading>
        {scheduleCallUrl && (
            <Box textAlign="center">
                <Button
                    as="a"
                    href={scheduleCallUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    mt={6}
                    variant="outline"
                    colorScheme="brand"
                    h={12}
                >
                    <FormattedMessage id="login-power-panel.schedule-call" />
                </Button>
            </Box>
        )}
    </>
);

const PersonImage: FunctionComponent<{ width?: number; height?: string }> = ({
    width = 260,
    height
}) =>
    height ? (
        <Image src={personImageSrc} alt="" h={height} w="auto" display="block" />
    ) : (
        <Image src={personImageSrc} alt="" w={`${width}px`} h="auto" display="block" />
    );

type LoginPowerPanelProps = {
    scheduleCallUrl?: string;
};

/** Desktop (lg+) two-column "Strefa Partnera" hero: teaser + representative on the left, styled login form on the right. */
export const LoginPowerPanel: FunctionComponent<LoginPowerPanelProps> = ({ scheduleCallUrl }) => {
    return (
        <Flex h="full" minH="700px">
            <VisuallyHidden>
                <H1>
                    <FormattedMessage id="page.login.title" />
                </H1>
            </VisuallyHidden>
            <Box bg="#d0ae66" position="relative" overflow="hidden" flex="1">
                <Flex justify="center" pt={24}>
                    <BrandLogo />
                </Flex>
                <Flex justify="center" position="relative" zIndex={1} mt={20} px={4}>
                    <Box maxW="560px">
                        <TeaserCta scheduleCallUrl={scheduleCallUrl} />
                    </Box>
                </Flex>
                <Flex position="absolute" bottom={0} left={0} align="flex-end">
                    <Box flexShrink={0} lineHeight={0}>
                        <PersonImage height="65vh" />
                    </Box>
                    <Box pl={1} pb={10}>
                        <RepresentativeContact light large />
                    </Box>
                </Flex>
            </Box>
            <Box
                bg="#0d0d0c"
                bgImage={`url(${bgImageSrc})`}
                bgSize="100% auto"
                bgPosition="top center"
                bgRepeat="no-repeat"
                flex="1"
            >
                <Flex direction="column" align="center" h="full" pt="660px" pb={10} px={8}>
                    <Heading
                        color="white"
                        fontSize="3xl"
                        letterSpacing="widest"
                        fontWeight="normal"
                        mb={6}
                    >
                        <FormattedMessage id="login-power-panel.strefa-partnera" />
                    </Heading>
                    <Box w="full" maxW="640px">
                        <LoginForm variant="power-panel" />
                    </Box>
                </Flex>
            </Box>
        </Flex>
    );
};

/** Mobile-only teaser shown below the plain login form, reusing the same power-panel copy. */
export const OvilineTeaser: FunctionComponent<LoginPowerPanelProps> = ({ scheduleCallUrl }) => (
    <Box bg="#d0ae66" px={6} py={10} textAlign="center">
        <Box display="flex" justifyContent="center" mb={8}>
            <BrandLogo w="260px" />
        </Box>
        <Heading
            as="h2"
            fontWeight="normal"
            fontSize="2xl"
            lineHeight={1.3}
            color="white"
            whiteSpace="pre-line"
            mb={4}
        >
            <FormattedMessage id="login-power-panel.title" />
        </Heading>
        {scheduleCallUrl && (
            <Button
                as="a"
                href={scheduleCallUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outline"
                colorScheme="brand"
                h={12}
            >
                <FormattedMessage id="login-power-panel.schedule-call" />
            </Button>
        )}
        <Box mt={0} display="flex" justifyContent="center">
            <PersonImage width={200} />
        </Box>
        <Box mt={4}>
            <RepresentativeContact light />
        </Box>
    </Box>
);
